import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { Link } from 'react-router-dom';

const BASE_URL = import.meta.env.VITE_SITE_URL ?? 'https://roameurope.blog';

const schemaJson = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Disclaimer — roameurope.blog',
  description: 'Read the full disclaimer for roameurope.blog. Content is for informational purposes only and not a substitute for professional advice.',
  url: `${BASE_URL}/disclaimer`,
  dateModified: '2026-04-09',
  publisher: {
    '@type': 'Organization',
    name: 'roameurope.blog',
    url: BASE_URL,
  },
  inLanguage: 'en-US',
});

export default function DisclaimerPage() {
  useSeoMeta({
    title: 'Disclaimer — roameurope.blog',
    description: 'Read the full disclaimer for roameurope.blog. Content is for informational purposes only and not a substitute for professional advice.',
    url: '/disclaimer',
    type: 'website',
    noindex: true,
  });

  const lastUpdated = 'April 9, 2026';

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schemaJson }}
      />
      <Navbar />
      <main className="pt-28 pb-20 min-h-screen bg-white dark:bg-stone-950 transition-colors duration-300">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h1 className="text-4xl font-bold text-stone-900 dark:text-stone-50 mb-2" style={{ fontFamily: "'Lora', serif" }}>
            Disclaimer
          </h1>
          <p className="text-xs text-stone-400 dark:text-stone-500 mb-10">Last updated: {lastUpdated}</p>

          <div className="space-y-8 text-stone-600 dark:text-stone-400 leading-relaxed text-sm">

            <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/40 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center text-rose-500 flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-base" />
                </div>
                <p className="text-sm text-stone-700 dark:text-stone-300 font-medium leading-relaxed">
                  The content on roameurope.blog is intended for general informational and educational purposes only. It is <strong>not</strong> a substitute for professional psychological, therapeutic, medical, or legal advice.
                </p>
              </div>
            </div>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">Not Professional Advice</h2>
              <p>Nothing on this Site constitutes professional relationship counselling, psychotherapy, medical advice, or legal advice. The articles, opinions, and information published here are for general educational purposes only. Always consult a qualified professional before making decisions about your mental health, relationships, or personal wellbeing.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">Accuracy of Information</h2>
              <p>While we make every effort to ensure the accuracy and currency of the information published on this Site, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, or suitability of the information. Any reliance you place on such information is strictly at your own risk.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">Affiliate &amp; Advertising Disclosure</h2>
              <p>This Site displays advertisements served by Google AdSense and may in the future contain affiliate links. If you click on an affiliate link and make a purchase, we may receive a small commission at no additional cost to you. We only reference products or services we believe may be of value to our readers. All advertising and affiliate relationships are clearly disclosed in accordance with FTC guidelines.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">External Links</h2>
              <p>This Site may contain links to external websites. These links are provided for convenience and informational purposes only. We have no control over the content of those sites and accept no responsibility for them or for any loss or damage that may arise from your use of them.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">Mental Health Resources</h2>
              <p>If you are experiencing a mental health crisis, relationship abuse, or any emergency situation, please seek immediate help from a qualified professional or emergency service. In the US, you can contact the <strong>988 Suicide &amp; Crisis Lifeline</strong> by calling or texting <strong>988</strong>. For relationship abuse support, contact the <strong>National Domestic Violence Hotline</strong> at 1-800-799-7233.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">Changes to This Disclaimer</h2>
              <p>We reserve the right to update this Disclaimer at any time. Changes will be posted on this page with an updated date.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 dark:text-stone-200 mb-2">Contact</h2>
              <p>If you have questions about this Disclaimer, please <Link to="/contact" className="text-rose-500 hover:underline">contact us</Link>.</p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
